import type {RawDraftContentState, RawDraftInlineStyleRange} from "draft-js";
import type {SerializedEditorState} from "lexical";
import {ParagraphProperties, TextProperties, TextStructure} from "../models/CellContent/TextStructure";
import {
    AlignStyleType, BoldType,
    ColorStyleType,
    FontStyleType,
    SHORT_ALIGN_STYLE_TYPE, SizeStyleType
} from "../models/CellContent/TextStyleType";


function blockTypeToAlignStyleTypeOrNull(type: string): AlignStyleType | null {

    const align_style_short_name_mapping: { [key: string]: SHORT_ALIGN_STYLE_TYPE } = {
        'align_left': 'left',
        'align_center': 'center',
        'align_right': 'right',
    };

    if (align_style_short_name_mapping[type] !== undefined) {
        return AlignStyleType.fromShortName(align_style_short_name_mapping[type]);
    }

    return null;
}


function createTextProperties(text: string, styles: string[]) {

    let color: ColorStyleType | null = null;
    let font: FontStyleType | null = null;
    let size: SizeStyleType | null = null;
    let weight: BoldType = BoldType.normal();
    for (const style of styles) {
        if (style.startsWith('color_style-')) {
            color = ColorStyleType.fromStringOrDefault(style, null);
        } else if (style.startsWith('size_style-')) {
            size = SizeStyleType.fromStringOrDefault(style, null);
        } else if (style.startsWith('font_style-')) {
            if (style === 'font_style-serif') {
                font = FontStyleType.fromShortName('serif');
            } else if (style === 'font_sans_serif' || style === 'font_style-sans-serif') {
                font = FontStyleType.fromShortName('sans_serif');
            }
        } else if (style === 'font_weight-bold') {
            weight = BoldType.bold();
        }

    }

    return {text, font, size, color, weight};
}

function createParagraph(block_type: string, text: string, ranges: RawDraftInlineStyleRange[]): ParagraphProperties {

    function sameStyle(a: string[], b: string[]): boolean {
        return a.join('\\') === b.join('\\');
    }

    const text_properties: TextProperties[] = [];

    const text_length = text.length;
    let prev_styles = null;
    let substring = '';
    for (let current = 0; current < text_length; ++current) {
        const char = text[current];
        const char_styles = ranges.filter(r => r.offset <= current && current < r.offset + r.length).map(r => r.style);

        if (prev_styles === null || sameStyle(prev_styles, char_styles)) {
            substring += char;
            prev_styles = char_styles;
        } else {
            text_properties.push(createTextProperties(substring, prev_styles));
            substring = char;
            prev_styles = char_styles;
        }
    }

    if (0 < substring.length && prev_styles !== null) {
        text_properties.push(createTextProperties(substring, prev_styles));
    }

    return {align: blockTypeToAlignStyleTypeOrNull(block_type), text: text_properties};
}

function convertRawDraftContentStateToTextStructure(draft: RawDraftContentState): TextStructure {

    const paragraphs: ParagraphProperties[] = [];
    for (const block of draft.blocks) {
        paragraphs.push(createParagraph(block.type, block.text, block.inlineStyleRanges));
    }

    return new TextStructure({root: {align: null}, paragraph: paragraphs},);

}

export function convertRawDraftContentStateToLexicalEditorState(draft: RawDraftContentState): SerializedEditorState {

    if (!Array.isArray(draft.blocks)) {
        throw new Error('no block in RawDraftContentState');
    }

    return convertRawDraftContentStateToTextStructure(draft).toRawLexicalEditorState(2);
}
